import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';
import { GrpcWrapperService } from './grpc-wrapper.service';

// Import generated gRPC client and protobuf types
import { BillingServiceClient } from '../../generated.commonjs/BillingServiceClientPb';
import {
  GetUserBillingProfileRequest,
  GetUserBillingProfileResponse,
  GetTransactionHistoryRequest,
  GetTransactionHistoryResponse,
  GetExpenseHistoryRequest,
  GetExpenseHistoryResponse,
  TopUpBalanceRequest,
  TopUpBalanceResponse,
  CheckSufficientBalanceRequest,
  CheckSufficientBalanceResponse,
  ReleaseTaskCostHoldRequest,
  ReleaseTaskCostHoldResponse,
} from '../../generated.commonjs/billing_pb';
import { Money } from '../../generated.commonjs/google/type/money_pb';

/**
 * Service for billing-related gRPC operations.
 * Uses the same pattern as TaskService - injected AuthInterceptor and stored client.
 */
@Injectable({
  providedIn: 'root',
})
export class BillingGrpcService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private grpcWrapper = inject(GrpcWrapperService);

  private static instanceCounter = 0;
  private instanceId: number;
  private client: BillingServiceClient;

  constructor() {
    this.instanceId = ++BillingGrpcService.instanceCounter;
    ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Constructor called`);

    // Initialize gRPC client with authentication (same pattern as TaskService)
    const apiServer = this.environmentService.getApiServer();
    ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Using gRPC API server: ${apiServer}`);

    // Configure gRPC client with auth interceptor
    const options = {
      unaryInterceptors: [this.authInterceptor],
    };

    this.client = new BillingServiceClient(`https://${apiServer}:443`, null, options);
    ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Initialized successfully`);
  }

  /**
   * Gets the user's billing profile and current balance.
   * Uses the same pattern as TaskService.getTaskStatus()
   */
  getUserBillingProfile(request: any = {}): Observable<GetUserBillingProfileResponse> {
    ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Getting user billing profile`);
    ClientLoggingService.log('DEBUG', `[BillingGrpcService ${this.instanceId}] Request options:`, request);

    return new Observable<GetUserBillingProfileResponse>(observer => {
      try {
        // Create the request using protobuf
        const grpcRequest = new GetUserBillingProfileRequest();
        ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] gRPC request created`);

        // Make the gRPC call (authentication handled by interceptor)
        ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Making gRPC call...`);
        const call = this.client.getUserBillingProfile(grpcRequest, null);

        call
          .then((response: GetUserBillingProfileResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[BillingGrpcService ${this.instanceId}] Billing profile retrieved:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[BillingGrpcService ${this.instanceId}] Failed to get billing profile:` +
                ` code=${error?.code}, message=${error?.message}`
            );
            observer.error(error);
          });
      } catch (error: any) {
        ClientLoggingService.log(
          'ERROR',
          `[BillingGrpcService ${this.instanceId}] Error getting billing profile:` +
            ` code=${error?.code}, message=${error?.message}`
        );
        observer.error(error);
      }
    });
  }

  /**
   * Gets the user's transaction history.
   */
  getTransactionHistory(request: any = {}): Observable<GetTransactionHistoryResponse> {
    ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Getting transaction history`);

    return new Observable<GetTransactionHistoryResponse>(observer => {
      try {
        // Create the request using protobuf
        const grpcRequest = new GetTransactionHistoryRequest();

        if (request.limit) {
          grpcRequest.setLimit(request.limit);
        }

        // Cursor for the requested page; omitted on the first page (Issue #1533).
        if (request.pageToken) {
          grpcRequest.setPageToken(request.pageToken);
        }

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.client.getTransactionHistory(grpcRequest, null);

        call
          .then((response: GetTransactionHistoryResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[BillingGrpcService ${this.instanceId}] Transaction history retrieved:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[BillingGrpcService ${this.instanceId}] Failed to get transaction history:`,
              error
            );
            observer.error(error);
          });
      } catch (error) {
        ClientLoggingService.log(
          'ERROR',
          `[BillingGrpcService ${this.instanceId}] Error getting transaction history:`,
          error
        );
        observer.error(error);
      }
    });
  }

  /**
   * Initiates a balance top-up via Stripe-hosted Checkout.
   * Creates a Checkout Session and returns the URL to redirect the browser to.
   */
  topUpBalance(request: any): Observable<TopUpBalanceResponse> {
    ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Initiating balance top-up`);

    return new Observable<TopUpBalanceResponse>(observer => {
      try {
        // Create the protobuf request
        const grpcRequest = new TopUpBalanceRequest();

        // Set the amount
        const money = new Money();
        money.setCurrencyCode(request.amount.currencyCode);
        money.setUnits(request.amount.units);
        money.setNanos(request.amount.nanos);
        grpcRequest.setAmount(money);

        // Set return URL (base URL Checkout returns the browser to)
        if (request.returnUrl) {
          grpcRequest.setReturnUrl(request.returnUrl);
        }

        ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Request:`, grpcRequest.toObject());

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.client.topUpBalance(grpcRequest, null);

        call
          .then((response: TopUpBalanceResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[BillingGrpcService ${this.instanceId}] Top-up initiated:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[BillingGrpcService ${this.instanceId}] Failed to initiate top-up:`,
              error
            );
            observer.error(error);
          });
      } catch (error) {
        ClientLoggingService.log('ERROR', `[BillingGrpcService ${this.instanceId}] Error initiating top-up:`, error);
        observer.error(error);
      }
    });
  }

  /**
   * Gets expense history grouped by projects.
   */
  getExpenseHistory(request: any = {}): Observable<GetExpenseHistoryResponse> {
    ClientLoggingService.log(
      'INFO',
      `[BillingGrpcService ${this.instanceId}] Getting expense history with limit:`,
      request.limit
    );

    return new Observable<GetExpenseHistoryResponse>(observer => {
      try {
        const grpcRequest = new GetExpenseHistoryRequest();
        if (request.limit) {
          grpcRequest.setLimit(request.limit);
        }
        if (request.pageToken) {
          grpcRequest.setPageToken(request.pageToken);
        }
        if (request.includeCollaborators) {
          grpcRequest.setIncludeCollaborators(request.includeCollaborators);
        }

        const call = this.client.getExpenseHistory(grpcRequest, null);

        call
          .then((response: GetExpenseHistoryResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[BillingGrpcService ${this.instanceId}] Got expense history:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[BillingGrpcService ${this.instanceId}] Failed to get expense history:`,
              error
            );
            observer.error(error);
          });
      } catch (error) {
        ClientLoggingService.log(
          'ERROR',
          `[BillingGrpcService ${this.instanceId}] Error getting expense history:`,
          error
        );
        observer.error(error);
      }
    });
  }

  getProjectExpenseHistory(request: any): Observable<any> {
    ClientLoggingService.log('DEBUG', '[BillingGrpcService] getProjectExpenseHistory stub called with:', request);
    return new Observable(observer => observer.complete());
  }

  checkSufficientBalance(request: any): Observable<CheckSufficientBalanceResponse> {
    ClientLoggingService.log('INFO', `[BillingGrpcService ${this.instanceId}] Checking sufficient balance`);

    return new Observable<CheckSufficientBalanceResponse>(observer => {
      try {
        const grpcRequest = new CheckSufficientBalanceRequest();

        if (request.userEmail) {
          grpcRequest.setUserEmail(request.userEmail);
        }

        if (request.estimatedCost) {
          const money = new Money();
          money.setCurrencyCode(request.estimatedCost.currencyCode || 'USD');
          money.setUnits(request.estimatedCost.units || 0);
          money.setNanos(request.estimatedCost.nanos || 0);
          grpcRequest.setEstimatedCost(money);
        }
        if (request.taskType) {
          grpcRequest.setTaskType(request.taskType);
        }
        if (request.taskCount) {
          grpcRequest.setTaskCount(request.taskCount);
        }
        if (request.operationId) {
          grpcRequest.setOperationId(request.operationId);
        }

        const call = this.client.checkSufficientBalance(grpcRequest, null);

        call
          .then((response: CheckSufficientBalanceResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[BillingGrpcService ${this.instanceId}] Balance check result:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[BillingGrpcService ${this.instanceId}] Failed to check balance:`,
              error
            );
            observer.error(error);
          });
      } catch (error) {
        ClientLoggingService.log('ERROR', `[BillingGrpcService ${this.instanceId}] Error checking balance:`, error);
        observer.error(error);
      }
    });
  }

  releaseTaskCostHold(taskId: string, reason: string): Observable<ReleaseTaskCostHoldResponse> {
    return new Observable<ReleaseTaskCostHoldResponse>(observer => {
      try {
        const grpcRequest = new ReleaseTaskCostHoldRequest();
        grpcRequest.setTaskId(taskId);
        grpcRequest.setReason(reason);
        this.client
          .releaseTaskCostHold(grpcRequest, null)
          .then((response: ReleaseTaskCostHoldResponse) => {
            observer.next(response);
            observer.complete();
          })
          .catch((error: unknown) => observer.error(error));
      } catch (error) {
        observer.error(error);
      }
    });
  }
}
